import {inject, Injectable} from '@angular/core';
import {catchError, from, Observable, switchMap, take, throwError} from 'rxjs';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  updatePassword,
  User,
  UserCredential
} from 'firebase/auth';
import {Auth, idToken, user} from '@angular/fire/auth';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly auth: Auth = inject(Auth);
  private readonly userPipe = user(this.auth);
  private readonly idTokenPipe = idToken(this.auth);

  public getUser(): Observable<User | null> {
    return this.userPipe.pipe(take(1));
  }

  public getAsyncToken(): Observable<string | null> {
    return this.idTokenPipe.pipe(take(1));
  }

  configureTenantId(tenantId: string): void {
    this.auth.tenantId = tenantId;
  }

  login(email: string, password: string): Observable<UserCredential> {
    return from(signInWithEmailAndPassword(this.auth, email, password));
  }

  loginWithToken(loginToken: string): Observable<UserCredential> {
    return from(signInWithCustomToken(this.auth, loginToken));
  }

  logout(): Observable<void> {
    return from(this.auth.signOut());
  }

  public changePassword(newPassword: string): Observable<void> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return throwError(() => 'Not logged in');
    }
    return from(updatePassword(currentUser, newPassword));
  }

  public reauthenticateAndChangePassword(currentPassword: string, newPassword: string): Observable<void> {
    const email: string | null | undefined = this.auth.currentUser?.email;
    if (!email) {
      return throwError(() => new ChangePasswordError('reauthentication', new Error('The authenticated user does not have an email address.')));
    }

    return this.reauthenticate(email, currentPassword).pipe(
      catchError((error: unknown) => throwError(() => new ChangePasswordError('reauthentication', error))),
      switchMap(() => this.changePassword(newPassword).pipe(
        catchError((error: unknown) => throwError(() => new ChangePasswordError('update', error)))
      ))
    );
  }

  resetPassword(email: string): Observable<void> {
    return from(sendPasswordResetEmail(this.auth, email));
  }

  public reauthenticate(email: string, password: string): Observable<UserCredential> {
    const credentials = EmailAuthProvider.credential(
      email, password);
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return throwError(() => 'Not logged in');
    }
    return from(reauthenticateWithCredential(currentUser, credentials));
  }
}

export type ChangePasswordErrorContext = 'reauthentication' | 'update';

export class ChangePasswordError {
  public constructor(public readonly context: ChangePasswordErrorContext, public readonly cause: unknown) {
  }
}
